import React, { createContext, useContext, useMemo, useState } from "react";

type WarmupState = "idle" | "running" | "success" | "error";

export type OfflineWarmupStatus = {
  state: WarmupState;
  total: number;
  completed: number;
  currentKey: string | null;
  errorMessage: string | null;
};

type OfflineWarmupDispatch = {
  start: (total: number) => void;
  progress: (completed: number, currentKey: string | null) => void;
  complete: () => void;
  error: (message?: string) => void;
  reset: () => void;
};

const defaultStatus: OfflineWarmupStatus = {
  state: "idle",
  total: 0,
  completed: 0,
  currentKey: null,
  errorMessage: null,
};

const OfflineWarmupStatusContext = createContext<OfflineWarmupStatus>(defaultStatus);
const OfflineWarmupDispatchContext = createContext<OfflineWarmupDispatch | undefined>(undefined);

export const OfflineWarmupStatusProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<OfflineWarmupStatus>(defaultStatus);

  const dispatchValue = useMemo<OfflineWarmupDispatch>(
    () => ({
      start: (total) =>
        setStatus({
          state: total > 0 ? "running" : "success",
          total,
          completed: 0,
          currentKey: null,
          errorMessage: null,
        }),
      progress: (completed, currentKey) =>
        setStatus((prev) => ({
          ...prev,
          completed: Math.min(completed, prev.total),
          currentKey,
        })),
      complete: () =>
        setStatus((prev) => ({
          ...prev,
          state: "success",
          completed: prev.total,
          currentKey: null,
          errorMessage: null,
        })),
      error: (message) =>
        setStatus((prev) => ({
          ...prev,
          state: "error",
          currentKey: null,
          errorMessage: message ?? "Warmup failed",
        })),
      reset: () => setStatus(defaultStatus),
    }),
    [],
  );

  return (
    <OfflineWarmupStatusContext.Provider value={status}>
      <OfflineWarmupDispatchContext.Provider value={dispatchValue}>
        {children}
      </OfflineWarmupDispatchContext.Provider>
    </OfflineWarmupStatusContext.Provider>
  );
};

export const useOfflineWarmupStatus = (): OfflineWarmupStatus => useContext(OfflineWarmupStatusContext);

export const useOfflineWarmupDispatch = (): OfflineWarmupDispatch => {
  const context = useContext(OfflineWarmupDispatchContext);
  if (!context) {
    throw new Error("useOfflineWarmupDispatch must be used within an OfflineWarmupStatusProvider");
  }
  return context;
};
